import type {
  Day,
  GetDailyDashboardParams,
  GetDailyDashboardResponse
} from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { errorToastMeta } from 'common/services/queryClient'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import {
  type AllFilters,
  convertPropsToParams
} from '../utils/dashboardFilters'

export const GET_DAILY_TASKS = `/api/list/tasks/dashboard`

type PaginationProps = {
  sortOrder?: '-1' | '1'
}

export type UseDailyTasksProps = (AllFilters & PaginationProps) | undefined
const useDailyTasks = (props: UseDailyTasksProps) => {
  const [days, setDays] = useState<Day[]>([])
  const {
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isFetchedAfterMount
  } = useInfiniteQuery<GetDailyTasksQueryResponse>({
    staleTime: 0,
    meta: errorToastMeta,
    queryKey: [`daily-tasks`, props],
    queryFn: ({ pageParam }) => {
      const convertedProps = convertPropsToDailyParams({
        ...props,
        cursor: pageParam
      })

      const queryParams = getQueryParams(convertedProps)

      return getJSON({
        url: queryParams ? `${GET_DAILY_TASKS}?${queryParams}` : GET_DAILY_TASKS
      })
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.result || !lastPage.result.pagination.hasNextPage) {
        return undefined
      }

      const lastDayIndex = lastPage.result.days.length
      const lastDay = lastPage.result.days[lastDayIndex - 1]

      // lastDay should never be undefined
      // because of a back-end bug, lastDay can be undefined and hasNextPage can be true
      // REQUIRES BACKEND-FIX
      return lastDay?.date
    },
    onSuccess: (data) => {
      let days: Day[] = []

      data?.pages.forEach((page) => {
        if (page.result?.days) {
          days = days.concat(page.result.days)
        }
      })

      setDays(days)
    },
    keepPreviousData: true
  })

  return {
    isLoading,
    days,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    hasNextPage,
    isRefetching,
    isFetchedAfterMount
  }
}

export const convertPropsToDailyParams = (
  props: UseDailyTasksProps & { cursor: string }
): GetDailyDashboardParams => {
  const baseParams = convertPropsToParams(props)

  let params: GetDailyDashboardParams = {
    ...baseParams,
    timezone: String(dayjs().utcOffset()),
    pagination: props?.sortOrder && {
      sortBy: ['date'],
      sortOrder: [props.sortOrder]
    }
  }

  if (props?.cursor) {
    const dateSortIndex = (params.pagination?.sortBy as Array<'date'>)?.indexOf(
      'date'
    )
    const dateSortOrder = params.pagination?.sortOrder?.[dateSortIndex || 0]
    let dateCursor = dayjs(props.cursor)

    if (dateSortOrder === '1') {
      dateCursor = dateCursor.endOf('day')
    } else {
      dateCursor = dateCursor.startOf('day')
    }

    params = {
      ...params,
      pagination: {
        ...params.pagination,
        cursor: dateCursor.toISOString()
      }
    }
  }

  return params
}

export type GetDailyTasksQueryResponse = {
  result?: GetDailyDashboardResponse
}

export enum TempCalculatedTaskStatus {
  overdue = 'overdue',
  pending = 'pending',
  skipped = 'skipped',
  completed = 'completed'
}

export default useDailyTasks
